import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Box, Breadcrumbs, CircularProgress, Grid, Link, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ArrowForwardIos } from '@mui/icons-material';
import dayjs from 'dayjs';

import { CrystalButtonPopup, Loading, ZoneCleaningHistory } from 'components';
import { ROUTES } from 'consts';
import { useCleaningHistoryViewData } from 'hooks/cleaning-history.hook';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useZone, useZones } from './zones.hook';
import { useParamValues } from 'hooks/url.hook';
import { BuildingCreateDialog } from 'dialogs/building';
import { ZoneCreateDialog } from 'dialogs/zone';
import { updateLayout } from 'store/slices/layout.slice';
import { useFetchZoneCleaningHistoryQuery } from 'store/apis';

interface ZoneCleaningHistoryPageProps {}

export const ZoneCleaningHistoryPage: React.FC<ZoneCleaningHistoryPageProps> = () => {
  const dispatch = useAppDispatch();
  const [loading_zones, { active: zones }] = useZones({ buildingId: null });
  const { zoneId: zoneIdstr } = useParamValues(['zoneId']);
  const { isAdmin, orgSlug } = useAppSelector(selectCurrentUserInfo);
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date());
  const zoneId = Number(zoneIdstr);
  const [loading_selectedZone, selectedZone] = useZone({ zoneId });

  const [visibleBuildingCreateDialog, setVisibleBuildingCreateDialog] = React.useState<boolean>(false);
  const [visibleZoneCreateDialog, setVisibleZoneCreateDialog] = React.useState<boolean>(false);

  const { data: cleaningHistory = null, isFetching: cleaningHistoryLoading } = useFetchZoneCleaningHistoryQuery({
    zoneId,
    date: dayjs(selectedDate).format('YYYY-MM-DD'),
  });
  const [cleaningHistoryViewData] = useCleaningHistoryViewData({
    zoneId,
    cleaningHistory,
  });

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Zone Cleaning History' }));
  }, []);

  React.useEffect(() => {
    if (zoneId <= 0 || Number.isNaN(zoneId)) {
      navigate(`/${orgSlug}/${ROUTES.ZONES}`);
    }
  }, [zoneId]);

  const pageMenuItems = [
    {
      id: 1,
      label: 'Building',
      onClick: () => setVisibleBuildingCreateDialog(true),
    },
    {
      id: 2,
      label: 'Zone',
      onClick: () => setVisibleZoneCreateDialog(true),
    },
  ];

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Breadcrumbs
                separator={
                  <Box sx={{ paddingX: 1.5 }}>
                    <ArrowForwardIos sx={{ fontSize: 10 }} />
                  </Box>
                }
              >
                <Link
                  component="button"
                  variant="subtitle2"
                  color="textSecondary"
                  onClick={() => navigate(`/${orgSlug}/${ROUTES.ZONES}`)}
                >
                  Zones
                </Link>

                {loading_selectedZone ? (
                  <CircularProgress size={16} sx={{ marginTop: 0.5 }} />
                ) : (
                  <Typography variant="subtitle2" color="primary">
                    {selectedZone ? `#${selectedZone.code} - ${selectedZone.name}` : ''}
                  </Typography>
                )}
              </Breadcrumbs>

              <Box sx={{ marginLeft: 'auto' }}>{isAdmin && <CrystalButtonPopup items={pageMenuItems} label="" />}</Box>
            </Box>
          </Grid>

          <Grid item xs={5}>
            <Autocomplete
              value={zones.find((zone) => zone.id === zoneId) || ''}
              options={zones}
              getOptionLabel={(option: any) => {
                const label = `${option.code || ''} - ${option.name || ''}`.trim();

                return label !== '-' ? `#${label}` : '';
              }}
              onChange={(event: any, newValue: any) => {
                if (newValue) {
                  navigate(`/${orgSlug}/${ROUTES.ZONE_CLEANING_HISTORY.replace(':zoneId', `${newValue.id}`)}`);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a Zone to view history"
                  placeholder="Enter search keyword..."
                  size="small"
                />
              )}
              fullWidth
              freeSolo
              loading={loading_zones}
              loadingText={<Loading text="Loading zones..." height={120} />}
            />
          </Grid>

          <Grid item xs={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newValue: any) => setSelectedDate(newValue)}
                renderInput={(params: any) => <TextField {...params} size="small" />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <ZoneCleaningHistory
              date={dayjs(selectedDate).format('dddd MMMM D, YYYY')}
              data={cleaningHistoryViewData}
              loading={cleaningHistoryLoading}
            />
          </Grid>
        </Grid>
      </Box>

      <BuildingCreateDialog visible={visibleBuildingCreateDialog} setVisible={setVisibleBuildingCreateDialog} />

      <ZoneCreateDialog visible={visibleZoneCreateDialog} setVisible={setVisibleZoneCreateDialog} />
    </>
  );
};
